// Block court / create event dialog (#1674).
//
// Lets staff reserve one or more of the facility's OWN courts for an event —
// name it, pick the courts + schedule, optionally invite players — with NO
// payment. It is a THIN form over POST /facilities/{id}/staff-court-blocks: the
// backend owns ALL validation (court_ids ≥1, non-blank name, end>start, invitee
// existence), RBAC (facility:schedule), and atomicity (a conflict on ANY court
// rejects the whole request, 409). The dialog only collects inputs, sends them,
// and on success asks the parent to re-list the day — it never fabricates state.
//
// Invited players are picked by NAME via the existing facility player search
// (/facilities/{id}/players/search) — only resolved display names are shown
// (no-UID-in-UI); the opaque user_id is sent, never rendered.

import {
  memo,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCourtTagLabel } from "@bw/shared/utils";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type { Court } from "@/libs/facilityApi";
import { fromZonedDateTimeInput } from "@/libs/zonedDateTime";
import {
  getStaffCourtBlockConflict,
  previewStaffCourtBlock,
  searchFacilityPlayers,
  staffBlockCourt,
  staffCourtBlockConflictMatchesAttempt,
  type FacilityPlayerSearchResult,
  type StaffCourtBlockCourtVerdict,
  type StaffCourtBlockPreview,
} from "@/libs/bookingsApi";

type PreviewStatus = "idle" | "loading" | "ready" | "error";

interface BlockCourtDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facilityId: string;
  facilityName: string;
  facilityTimeZone: string;
  /** Bookable courts (the parent already loaded these for the day view). */
  courts: Court[];
  /** Called after a successful block so the parent re-lists the day. */
  onCreated: () => void;
  /** Controlled dialogs need an explicit target for focus restoration. */
  returnFocusRef: RefObject<HTMLButtonElement | null>;
}

interface PreviewInput {
  key: string;
  courtIds: string[];
  startTime: string;
  endTime: string;
}

interface InviteResultPublication {
  facilityId: string;
  query: string;
  players: FacilityPlayerSearchResult[];
}

const COURT_SEARCH_SETTLE_MS = 100;

function formatCourtTag(tag: string): string {
  const label = formatCourtTagLabel(tag.trim());
  if (label !== tag.trim()) return label;
  const words = tag.trim().replace(/[_-]+/g, " ");
  return words ? `${words[0].toUpperCase()}${words.slice(1)}` : "";
}

function courtAvailabilityText(
  status: PreviewStatus,
  verdict: StaffCourtBlockCourtVerdict | undefined,
  timeFormatter: Intl.DateTimeFormat,
): string {
  if (status === "idle") return "Enter times to check availability";
  if (status === "loading") return "Checking availability";
  if (status === "error" || !verdict) return "Availability unavailable";
  if (verdict.available) return "Available";
  if (verdict.unavailable_reason === "court_disabled") return "Court disabled";
  if (verdict.unavailable_reason === "outside_operating_hours") {
    return "Outside operating hours";
  }
  const conflict = verdict.conflicts?.[0];
  if (!conflict) return "Unavailable";
  const source = formatCourtTag(conflict.source_type ?? conflict.event_type);
  const start = timeFormatter.format(new Date(conflict.start_time));
  const end = timeFormatter.format(new Date(conflict.end_time));
  return `Unavailable: ${source}, ${start}-${end}`;
}

function CourtSearchInput({
  disabled,
  pending,
  onPendingChange,
  onSettledQueryChange,
}: {
  disabled: boolean;
  pending: boolean;
  onPendingChange: (pending: boolean) => void;
  onSettledQueryChange: (query: string) => void;
}) {
  const [value, setValue] = useState("");
  const publishedQuery = useRef("");
  const normalizedQuery = value.trim().toLowerCase();

  useEffect(() => {
    if (normalizedQuery === publishedQuery.current) {
      onPendingChange(false);
      return;
    }
    const timeout = window.setTimeout(() => {
      publishedQuery.current = normalizedQuery;
      onSettledQueryChange(normalizedQuery);
      onPendingChange(false);
    }, COURT_SEARCH_SETTLE_MS);
    return () => window.clearTimeout(timeout);
  }, [normalizedQuery, onPendingChange, onSettledQueryChange]);

  return (
    <TextField
      type="search"
      label="Search courts"
      size="small"
      fullWidth
      placeholder="Search courts or tags"
      value={value}
      disabled={disabled}
      onChange={(event) => {
        const nextValue = event.target.value;
        setValue(nextValue);
        onPendingChange(
          nextValue.trim().toLowerCase() !== publishedQuery.current,
        );
      }}
      slotProps={{
        htmlInput: {
          "aria-label": "Search courts",
          "aria-busy": pending,
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" aria-hidden="true" />
            </InputAdornment>
          ),
          endAdornment: pending ? (
            <InputAdornment position="end">
              <CircularProgress
                aria-label="Updating court results"
                role="status"
                size={16}
              />
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
}

const CourtList = memo(function CourtList({
  courts,
  selectedCourtIds,
  previewVerdicts,
  previewStatus,
  selectionDisabled,
  timeFormatter,
  describedBy,
  onToggleCourt,
}: {
  courts: Court[];
  selectedCourtIds: Set<string>;
  previewVerdicts: Map<string, StaffCourtBlockCourtVerdict>;
  previewStatus: PreviewStatus;
  selectionDisabled: boolean;
  timeFormatter: Intl.DateTimeFormat;
  describedBy?: string;
  onToggleCourt: (courtId: string) => void;
}) {
  return (
    <Box
      role="group"
      aria-label="Courts"
      aria-describedby={describedBy}
      tabIndex={0}
      data-testid="block-court-list"
      sx={{
        maxHeight: { xs: "none", md: 320 },
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {courts.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ px: 2, py: 3, textAlign: "center" }}
        >
          No courts match your search.
        </Typography>
      ) : (
        courts.map((court) => {
          const checked = selectedCourtIds.has(court.court_id);
          const verdict = previewVerdicts.get(court.court_id);
          const unavailable = verdict?.available === false;
          const tagLabels = court.tags.map(formatCourtTag).filter(Boolean);
          const tagsId = `block-court-tags-${court.court_id}`;
          const statusId = `block-court-status-${court.court_id}`;
          const availabilityText = courtAvailabilityText(
            previewStatus,
            verdict,
            timeFormatter,
          );
          const interactionDisabled =
            selectionDisabled || (unavailable && !checked);
          const statusColor =
            previewStatus === "ready" && verdict?.available
              ? "success.main"
              : unavailable || previewStatus === "error"
                ? "error.main"
                : "text.secondary";
          return (
            <Box
              component="label"
              key={court.court_id}
              onClickCapture={(event) => {
                if (interactionDisabled) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "auto minmax(0, 1fr)",
                  sm: "auto minmax(0, 1fr) minmax(0, 9rem)",
                },
                minHeight: 48,
                alignItems: "start",
                columnGap: 1.5,
                px: 1.5,
                py: 1,
                cursor: unavailable && !checked ? "not-allowed" : "pointer",
                bgcolor:
                  unavailable && !checked ? "action.disabledBackground" : null,
                "&:not(:last-of-type)": {
                  borderBottom: 1,
                  borderColor: "divider",
                },
                "&:hover":
                  unavailable && !checked
                    ? undefined
                    : { bgcolor: "action.hover" },
              }}
            >
              <Checkbox
                checked={checked}
                disabled={interactionDisabled}
                onChange={() => onToggleCourt(court.court_id)}
                size="small"
                sx={{ p: 0.25, mt: 0.25 }}
                slotProps={{
                  input: {
                    "aria-label": court.name,
                    "aria-describedby": [
                      statusId,
                      tagLabels.length ? tagsId : null,
                    ]
                      .filter(Boolean)
                      .join(" "),
                  },
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
                >
                  {court.name}
                </Typography>
                {tagLabels.length > 0 ? (
                  <Box
                    id={tagsId}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {tagLabels.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    ))}
                  </Box>
                ) : null}
              </Box>
              <Typography
                id={statusId}
                color={statusColor}
                variant="caption"
                sx={{
                  gridColumn: { xs: 2, sm: 3 },
                  gridRow: { sm: 1 },
                  minWidth: 0,
                  mt: { xs: 0.5, sm: 0 },
                  justifySelf: { sm: "end" },
                  textAlign: { xs: "left", sm: "right" },
                  overflowWrap: "anywhere",
                }}
              >
                {availabilityText}
              </Typography>
            </Box>
          );
        })
      )}
    </Box>
  );
});

export function BlockCourtDialog({
  open,
  onOpenChange,
  facilityId,
  facilityName,
  facilityTimeZone,
  courts,
  onCreated,
  returnFocusRef,
}: BlockCourtDialogProps) {
  const [selectedCourtIds, setSelectedCourtIds] = useState<Set<string>>(
    new Set(),
  );
  const [courtQuery, setCourtQuery] = useState("");
  const [courtSearchPending, setCourtSearchPending] = useState(false);
  const [eventName, setEventName] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [debouncedPreviewKey, setDebouncedPreviewKey] = useState<string | null>(
    null,
  );
  const [reconciliationFailed, setReconciliationFailed] = useState(false);
  const queryClient = useQueryClient();
  // Synchronous re-entrancy guard (flips before any await) so a same-tick
  // double-click can't fire two POSTs.
  const inFlight = useRef(false);

  // --- Invitee picker (optional) ---------------------------------------------
  const [inviteQuery, setInviteQuery] = useState("");
  const currentInviteQuery = inviteQuery.trim();
  const inviteContextRef = useRef({
    facilityId,
    query: currentInviteQuery,
  });
  inviteContextRef.current = { facilityId, query: currentInviteQuery };
  const [inviteResultPublication, setInviteResultPublication] =
    useState<InviteResultPublication | null>(null);
  // user_id -> display_name for the chosen invitees (display_name is rendered;
  // user_id is what the POST sends).
  const [invitees, setInvitees] = useState<Map<string, string>>(new Map());

  const reset = useCallback(() => {
    setSelectedCourtIds(new Set());
    setCourtQuery("");
    setCourtSearchPending(false);
    setEventName("");
    setStartLocal("");
    setEndLocal("");
    setError(null);
    setSuccessMessage(null);
    setDebouncedPreviewKey(null);
    setReconciliationFailed(false);
    setInviteQuery("");
    setInviteResultPublication(null);
    setInvitees(new Map());
  }, []);

  // Reset the whole form each time the dialog opens, so a prior draft never leaks.
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  // Debounced facility player search for the invite picker (min 2 chars). Only
  // resolved display names come back (no-UID-in-UI); a stale response is dropped.
  useEffect(() => {
    const q = currentInviteQuery;
    if (q.length < 2) {
      setInviteResultPublication(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      searchFacilityPlayers(facilityId, q)
        .then((res) => {
          if (!cancelled) {
            setInviteResultPublication({
              facilityId,
              query: q,
              players: res,
            });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setInviteResultPublication({
              facilityId,
              query: q,
              players: [],
            });
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [currentInviteQuery, facilityId]);

  const visibleInviteResults =
    inviteResultPublication?.facilityId === facilityId &&
    inviteResultPublication.query === currentInviteQuery
      ? inviteResultPublication.players
      : [];

  const toggleCourt = useCallback((courtId: string) => {
    setSelectedCourtIds((prev) => {
      const next = new Set(prev);
      if (next.has(courtId)) next.delete(courtId);
      else next.add(courtId);
      return next;
    });
    setError(null);
  }, []);

  const bookableCourts = useMemo(
    () => courts.filter((court) => court.status === "enabled"),
    [courts],
  );
  const previewInput = useMemo<PreviewInput | null>(() => {
    const startTime = fromZonedDateTimeInput(startLocal, facilityTimeZone);
    const endTime = fromZonedDateTimeInput(endLocal, facilityTimeZone);
    if (
      !startTime ||
      !endTime ||
      Date.parse(endTime) <= Date.parse(startTime) ||
      bookableCourts.length === 0
    ) {
      return null;
    }
    const courtIds = bookableCourts.map((court) => court.court_id);
    return {
      key: JSON.stringify([facilityId, startTime, endTime, courtIds]),
      courtIds,
      startTime,
      endTime,
    };
  }, [bookableCourts, endLocal, facilityId, facilityTimeZone, startLocal]);

  useEffect(() => {
    setReconciliationFailed(false);
    if (!open || !previewInput) {
      setDebouncedPreviewKey(null);
      return;
    }
    const timeout = window.setTimeout(() => {
      setDebouncedPreviewKey(previewInput.key);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [open, previewInput]);

  const previewQueryKey = [
    "staff-court-block-preview",
    facilityId,
    debouncedPreviewKey,
  ] as const;
  const previewQuery = useQuery({
    queryKey: previewQueryKey,
    queryFn: () => {
      if (!previewInput || previewInput.key !== debouncedPreviewKey) {
        throw new Error("Court availability request was superseded.");
      }
      return previewStaffCourtBlock(facilityId, {
        court_ids: previewInput.courtIds,
        start_time: previewInput.startTime,
        end_time: previewInput.endTime,
      });
    },
    enabled:
      open &&
      previewInput !== null &&
      debouncedPreviewKey !== null &&
      previewInput.key === debouncedPreviewKey,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const previewStatus: PreviewStatus = !previewInput
    ? "idle"
    : reconciliationFailed || previewQuery.isError
      ? "error"
      : previewInput.key !== debouncedPreviewKey ||
          previewQuery.isPending ||
          previewQuery.isFetching
        ? "loading"
        : previewQuery.data
          ? "ready"
          : "error";
  const previewTimezone = previewQuery.data?.timezone ?? facilityTimeZone;
  const previewVerdicts = useMemo(
    () =>
      previewStatus === "ready" && previewQuery.data
        ? new Map(previewQuery.data.courts.map((row) => [row.court_id, row]))
        : new Map<string, StaffCourtBlockCourtVerdict>(),
    [previewQuery.data, previewStatus],
  );
  const filteredCourts = useMemo(() => {
    if (!courtQuery) return bookableCourts;

    return bookableCourts.filter((court) =>
      [court.name, ...court.tags, ...court.tags.map(formatCourtTag)].some(
        (value) => value.toLowerCase().includes(courtQuery),
      ),
    );
  }, [bookableCourts, courtQuery]);
  const availableFilteredCourts = useMemo(
    () =>
      previewStatus === "ready"
        ? filteredCourts.filter(
            (court) => previewVerdicts.get(court.court_id)?.available === true,
          )
        : [],
    [filteredCourts, previewStatus, previewVerdicts],
  );
  const allAvailableMatchesSelected =
    availableFilteredCourts.length > 0 &&
    availableFilteredCourts.every((court) =>
      selectedCourtIds.has(court.court_id),
    );
  const previewCounts = useMemo(() => {
    const rows = filteredCourts
      .map((court) => previewVerdicts.get(court.court_id))
      .filter((row): row is StaffCourtBlockCourtVerdict => row !== undefined);
    return {
      available: rows.filter((row) => row.available).length,
      unavailable: rows.filter((row) => !row.available).length,
    };
  }, [filteredCourts, previewVerdicts]);
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
        timeZone: previewTimezone,
      }),
    [previewTimezone],
  );

  const selectAvailableMatches = () => {
    setSelectedCourtIds((previous) => {
      const next = new Set(previous);
      availableFilteredCourts.forEach((court) => next.add(court.court_id));
      return next;
    });
    setError(null);
  };

  const clearSelectedCourts = () => {
    setSelectedCourtIds(new Set());
    setError(null);
  };

  const retryAvailability = async () => {
    setError(null);
    const result = await previewQuery.refetch();
    setReconciliationFailed(result.isError || !result.data);
  };

  const addInvitee = (player: FacilityPlayerSearchResult) => {
    const currentContext = inviteContextRef.current;
    if (
      !player.display_name ||
      inviteResultPublication?.facilityId !== currentContext.facilityId ||
      inviteResultPublication.query !== currentContext.query ||
      !inviteResultPublication.players.some(
        (result) => result.user_id === player.user_id,
      )
    ) {
      return;
    }
    setInvitees((prev) => {
      const next = new Map(prev);
      next.set(player.user_id, player.display_name as string);
      return next;
    });
    setInviteQuery("");
    setInviteResultPublication(null);
  };

  const removeInvitee = (userId: string) => {
    setInvitees((prev) => {
      const next = new Map(prev);
      next.delete(userId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (inFlight.current) return;

    // Early, honest guards (the backend re-enforces all of these). They only
    // stop an obviously-bad POST and give immediate feedback.
    const name = eventName.trim();
    if (selectedCourtIds.size === 0) {
      setError("Select at least one court to block.");
      return;
    }
    if (!name) {
      setError("Enter an event name.");
      return;
    }
    const startIso = fromZonedDateTimeInput(startLocal, facilityTimeZone);
    const endIso = fromZonedDateTimeInput(endLocal, facilityTimeZone);
    if (!startIso || !endIso) {
      setError("Enter a valid start and end time.");
      return;
    }
    if (new Date(endIso) <= new Date(startIso)) {
      setError("End time must be after the start time.");
      return;
    }
    if (previewStatus === "loading") {
      setError("Wait for court availability to finish checking.");
      return;
    }
    if (previewStatus !== "ready") {
      setError("Court availability could not be verified. Try again.");
      return;
    }
    const selectedVerdicts = [...selectedCourtIds].map((courtId) =>
      previewVerdicts.get(courtId),
    );
    if (
      selectedVerdicts.some(
        (verdict) => verdict === undefined || verdict.available === false,
      )
    ) {
      setError("Remove unavailable courts before blocking the event.");
      return;
    }

    const attempt = {
      courtIds: [...selectedCourtIds],
      startTime: startIso,
      endTime: endIso,
      eventName: name,
      invitedPlayerIds: [...invitees.keys()],
    };

    setError(null);
    inFlight.current = true;
    setSubmitting(true);
    try {
      await staffBlockCourt(facilityId, {
        court_ids: attempt.courtIds,
        start_time: attempt.startTime,
        end_time: attempt.endTime,
        event_name: attempt.eventName,
        invited_player_ids:
          attempt.invitedPlayerIds.length > 0 ? attempt.invitedPlayerIds : null,
      });
      const courtWord = attempt.courtIds.length === 1 ? "court" : "courts";
      setSuccessMessage(
        `Blocked ${attempt.courtIds.length} ${courtWord} for "${attempt.eventName}" with no charge.`,
      );
      onCreated();
      onOpenChange(false);
    } catch (err) {
      const conflict = getStaffCourtBlockConflict(err);
      if (conflict) {
        if (!staffCourtBlockConflictMatchesAttempt(conflict, attempt)) {
          setReconciliationFailed(true);
          setError(
            "The availability response did not match this request. Check availability and try again.",
          );
          return;
        }
        const conflictByCourt = new Map(
          conflict.conflicts.map((row) => [row.court_id, row]),
        );
        const reconciled = queryClient.setQueryData<StaffCourtBlockPreview>(
          previewQueryKey,
          (previous) => {
            if (!previous) return undefined;
            return {
              ...previous,
              timezone: conflict.timezone,
              courts: previous.courts.map(
                (row) => conflictByCourt.get(row.court_id) ?? row,
              ),
            };
          },
        );
        if (!reconciled) {
          setReconciliationFailed(true);
          setError(
            "The availability response did not match this request. Check availability and try again.",
          );
          return;
        }
        setReconciliationFailed(false);
        setError(
          "Court availability changed. Review the unavailable courts and try again.",
        );
        return;
      }
      // 403 RBAC / 409 conflict / 400 validation — surface honestly and STAY open
      // so the operator can adjust. No optimistic state was written.
      setError(
        err instanceof Error ? err.message : "Failed to block the court(s).",
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const eventNameValidationError =
    error === "Enter an event name." ? error : null;
  const intervalValidationError =
    error === "Enter a valid start and end time." ? error : null;
  const startValidationError = intervalValidationError;
  const endValidationError =
    error === "End time must be after the start time."
      ? error
      : intervalValidationError;
  const visibleError =
    error &&
    !eventNameValidationError &&
    !startValidationError &&
    !endValidationError
      ? error
      : null;
  const courtValidationError =
    visibleError &&
    [
      "Select at least one court to block.",
      "Wait for court availability to finish checking.",
      "Court availability could not be verified. Try again.",
      "Remove unavailable courts before blocking the event.",
    ].includes(visibleError)
      ? visibleError
      : null;

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        disableRestoreFocus
        aria-labelledby="block-court-dialog-title"
        aria-describedby="block-court-dialog-description"
        onClose={() => {
          if (!submitting) onOpenChange(false);
        }}
        slotProps={{
          paper: {
            sx: { maxHeight: "calc(100dvh - 2rem)" },
          },
          transition: {
            onExited: () => returnFocusRef.current?.focus(),
          },
        }}
      >
        <DialogTitle
          id="block-court-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            Block court / create event
          </Box>
          <IconButton
            aria-label="Close block court dialog"
            edge="end"
            size="small"
            disabled={submitting}
            onClick={() => {
              if (!submitting) onOpenChange(false);
            }}
          >
            <CloseRoundedIcon aria-hidden="true" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          data-testid="block-court-dialog-body"
          sx={{
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            alignItems: "start",
            gap: { xs: 3, md: 4 },
          }}
        >
          <DialogContentText
            id="block-court-dialog-description"
            sx={{ gridColumn: "1 / -1", m: 0 }}
          >
            At {facilityName}, reserve one or more courts for an event without
            charging a player.
          </DialogContentText>

          <Stack spacing={3} sx={{ minWidth: 0 }}>
            <TextField
              id="block-event-name"
              label="Event name"
              size="small"
              fullWidth
              placeholder="e.g. League night, Resurfacing"
              value={eventName}
              disabled={submitting}
              error={Boolean(eventNameValidationError)}
              helperText={eventNameValidationError}
              onChange={(event) => {
                setEventName(event.target.value);
                setError(null);
              }}
              slotProps={{
                formHelperText: { role: "alert" },
                htmlInput: {
                  "aria-label": "Event name",
                  maxLength: 255,
                },
              }}
            />

            <Box component="section" aria-labelledby="block-event-time-heading">
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 1,
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  id="block-event-time-heading"
                  component="h3"
                  variant="subtitle2"
                >
                  Time
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  Facility time ({facilityTimeZone})
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                <TextField
                  id="block-start"
                  label="Start"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={startLocal}
                  disabled={submitting}
                  error={Boolean(startValidationError)}
                  helperText={startValidationError}
                  onChange={(event) => {
                    setStartLocal(event.target.value);
                    setError(null);
                  }}
                  slotProps={{
                    formHelperText: { role: "alert" },
                    htmlInput: { "aria-label": "Start time" },
                    inputLabel: { shrink: true },
                  }}
                />
                <TextField
                  id="block-end"
                  label="End"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={endLocal}
                  disabled={submitting}
                  error={Boolean(endValidationError)}
                  helperText={endValidationError}
                  onChange={(event) => {
                    setEndLocal(event.target.value);
                    setError(null);
                  }}
                  slotProps={{
                    formHelperText: { role: "alert" },
                    htmlInput: { "aria-label": "End time" },
                    inputLabel: { shrink: true },
                  }}
                />
              </Box>
            </Box>

            <Box
              component="section"
              aria-labelledby="block-event-invitees-heading"
            >
              <Typography
                id="block-event-invitees-heading"
                component="h3"
                variant="subtitle2"
                sx={{ mb: 1 }}
              >
                Invite players (optional)
              </Typography>
              <Stack spacing={1}>
                <TextField
                  id="block-invite"
                  type="search"
                  label="Invite players"
                  size="small"
                  fullWidth
                  placeholder="Search players by name"
                  value={inviteQuery}
                  disabled={submitting}
                  onChange={(event) => setInviteQuery(event.target.value)}
                  slotProps={{
                    htmlInput: { "aria-label": "Invite players" },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon
                            fontSize="small"
                            aria-hidden="true"
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {visibleInviteResults.length > 0 ? (
                  <List
                    aria-label="Player results"
                    disablePadding
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {visibleInviteResults.map((player) => (
                      <ListItemButton
                        key={player.user_id}
                        disabled={submitting}
                        onClick={() => addInvitee(player)}
                      >
                        <ListItemText
                          primary={player.display_name}
                          slotProps={{ primary: { variant: "body2" } }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : null}
                {invitees.size > 0 ? (
                  <Stack direction="row" useFlexGap sx={{ flexWrap: "wrap" }}>
                    {[...invitees.entries()].map(([userId, name]) => (
                      <Chip
                        key={userId}
                        label={name}
                        size="small"
                        variant="outlined"
                        disabled={submitting}
                        onDelete={() => removeInvitee(userId)}
                        deleteIcon={
                          <CloseRoundedIcon aria-label={`Remove ${name}`} />
                        }
                      />
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Box
              component="section"
              aria-labelledby="block-event-courts-heading"
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 1,
                  alignItems: "baseline",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  id="block-event-courts-heading"
                  component="h3"
                  variant="subtitle2"
                >
                  Courts
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="caption"
                  aria-live="polite"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {selectedCourtIds.size} selected
                </Typography>
              </Stack>
              {bookableCourts.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No enabled courts to block.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  <CourtSearchInput
                    disabled={submitting}
                    pending={courtSearchPending}
                    onPendingChange={setCourtSearchPending}
                    onSettledQueryChange={setCourtQuery}
                  />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      alignItems: { xs: "stretch", sm: "center" },
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {filteredCourts.length} of {bookableCourts.length}{" "}
                        courts
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        aria-live="polite"
                      >
                        {previewStatus === "idle"
                          ? "Enter times to check"
                          : previewStatus === "loading"
                            ? `Checking ${bookableCourts.length} courts`
                            : previewStatus === "error"
                              ? "Availability check failed"
                              : `${previewCounts.available} available, ${previewCounts.unavailable} unavailable`}
                      </Typography>
                    </Stack>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.5}
                      sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                    >
                      {previewStatus === "error" ? (
                        <Button
                          type="button"
                          variant="text"
                          size="small"
                          startIcon={<RefreshRoundedIcon />}
                          onClick={() => void retryAvailability()}
                          disabled={previewQuery.isFetching || submitting}
                        >
                          Retry availability
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="text"
                          size="small"
                          onClick={selectAvailableMatches}
                          disabled={
                            previewStatus !== "ready" ||
                            availableFilteredCourts.length === 0 ||
                            allAvailableMatchesSelected ||
                            courtSearchPending ||
                            submitting
                          }
                        >
                          {previewStatus === "ready"
                            ? `Select all ${availableFilteredCourts.length} available`
                            : `Select all ${filteredCourts.length} matches`}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="text"
                        size="small"
                        onClick={clearSelectedCourts}
                        disabled={
                          selectedCourtIds.size === 0 ||
                          courtSearchPending ||
                          submitting
                        }
                      >
                        Clear selection
                      </Button>
                    </Stack>
                  </Stack>
                  <CourtList
                    courts={filteredCourts}
                    selectedCourtIds={selectedCourtIds}
                    previewVerdicts={previewVerdicts}
                    previewStatus={previewStatus}
                    selectionDisabled={courtSearchPending || submitting}
                    timeFormatter={timeFormatter}
                    describedBy={
                      courtValidationError
                        ? "block-court-validation-error"
                        : undefined
                    }
                    onToggleCourt={toggleCourt}
                  />
                </Stack>
              )}
            </Box>

            {visibleError ? (
              <Alert
                id={
                  courtValidationError
                    ? "block-court-validation-error"
                    : undefined
                }
                severity="error"
              >
                {visibleError}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, flexShrink: 0 }}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            size="small"
            onClick={() => void handleSubmit()}
            disabled={submitting}
          >
            {submitting ? "Blocking…" : "Block court"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={(_event, reason) => {
          if (reason !== "clickaway") setSuccessMessage(null);
        }}
      >
        {successMessage ? (
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
